<style scoped>
main {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.geo-status {
	font-size: 0.875rem;
}
.contact-scroll {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}
</style>
<template>
	<main>
		<h2 class="h5 mb-2">{{ t("contact_title") }}</h2>

		<div class="alert alert-light border mb-2 py-2">
			<p class="mb-0 small">{{ t("contact_body") }}</p>
		</div>

		<div class="contact-scroll">
			<div class="mb-2">
				<label class="form-label mb-1" for="mc-name">{{
					t("your_name")
				}}</label>
				<div class="input-group">
					<span class="input-group-text">
						<i class="bi bi-person-lines-fill"></i>
					</span>
					<input
						id="mc-name"
						v-model="form.name"
						class="form-control"
						type="text"
						autocomplete="name"
						placeholder="Adam Smith"
						@input="scheduleSave"
					/>
				</div>
			</div>

			<div class="mb-2">
				<label class="form-label mb-1" for="mc-email">
					{{ t("email_optional") }}
				</label>
				<div class="input-group">
					<span class="input-group-text"><i class="bi bi-envelope"></i></span>
					<input
						id="mc-email"
						v-model="form.email"
						class="form-control"
						type="email"
						autocomplete="email"
						placeholder="your@email.com"
						@input="scheduleSave"
					/>
				</div>
			</div>

			<div class="mb-2">
				<label class="form-label mb-1" for="mc-phone">
					{{ t("phone_number") }}
				</label>
				<div class="input-group">
					<span class="input-group-text"><i class="bi bi-telephone"></i></span>
					<input
						id="mc-phone"
						v-model="form.phone"
						class="form-control"
						:class="{ 'is-invalid': phoneError }"
						type="tel"
						autocomplete="tel"
						placeholder="(123) 456-7890"
						@input="scheduleSave"
					/>
				</div>
				<div class="invalid-feedback" v-if="phoneError">{{ phoneError }}</div>
			</div>

			<div class="mb-2">
				<label class="form-label mb-1" for="mc-address">
					{{ t("property_address") }}
				</label>
				<div class="input-group">
					<span class="input-group-text"><i class="bi bi-geo-alt"></i></span>
					<input
						id="mc-address"
						v-model="form.property_address"
						class="form-control"
						type="text"
						autocomplete="street-address"
						placeholder="123 Main St"
						@input="scheduleSave"
					/>
				</div>
			</div>

			<div class="mb-2">
				<button
					type="button"
					class="btn btn-sm btn-outline-primary"
					:disabled="locating"
					@click="useLocation"
				>
					<i class="bi bi-crosshair me-1"></i>
					{{ t("use_location") }}
					<span
						v-if="locating"
						class="spinner-border spinner-border-sm ms-1"
					></span>
				</button>
				<div v-if="locationStatus" class="geo-status text-muted mt-1">
					<i
						class="bi me-1"
						:class="
							locationOk
								? 'bi-check-circle text-success'
								: 'bi-exclamation-triangle text-warning'
						"
					></i>
					{{ locationStatus }}
				</div>
			</div>
		</div>

		<div class="d-flex gap-2 mt-2">
			<button type="button" class="btn btn-outline-secondary" @click="goBeyond">
				{{ t("back") }}
			</button>
			<button
				type="button"
				class="btn btn-primary"
				:disabled="submitting"
				@click="doSubmit"
			>
				<span
					v-if="submitting"
					class="spinner-border spinner-border-sm me-1"
				></span>
				{{ t("submit") }}
			</button>
		</div>
	</main>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";

import { useT } from "@/rmo/content/mosquito/copy";
import { phoneValidationError } from "@/phone";
import { router } from "@/rmo/route/config";
import { useRoutes } from "@/rmo/route/use";
import { useStoreGeocode } from "@/store/geocode";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";
import type { VirtualInspectionContactRequest } from "@/type/api";

interface Props {
	token: string;
}

const props = defineProps<Props>();
const routes = useRoutes();
const store = useStoreVirtualInspection();
const geocode = useStoreGeocode();
const t = useT();
const locating = ref(false);
const locationOk = ref(false);
const locationStatus = ref("");
const submitting = ref(false);
const phoneError = ref("");
let debounceTimer: number | undefined = undefined;

const form = reactive({
	name: "",
	phone: "",
	email: "",
	property_address: "",
});

function initFromState() {
	const st = store.state;
	if (!st) {
		return;
	}
	form.name = st.contact?.name ?? "";
	form.phone = st.contact?.phone ?? "";
	form.email = st.contact?.email ?? "";
	form.property_address = st.property_address ?? "";
	if (st.geom) {
		locationOk.value = true;
		locationStatus.value = t("location_captured");
	}
}

function scheduleSave() {
	if (debounceTimer) {
		window.clearTimeout(debounceTimer);
	}
	debounceTimer = window.setTimeout(() => {
		debounceTimer = undefined;
		flushSave();
	}, 600);
}

function flushSave(): Promise<void> {
	if (debounceTimer) {
		window.clearTimeout(debounceTimer);
		debounceTimer = undefined;
	}
	const req: VirtualInspectionContactRequest = {
		name: form.name,
		phone: form.phone,
		email: form.email,
		property_address: form.property_address,
	};
	return store.saveContact(props.token, req);
}

function useLocation() {
	if (!("geolocation" in navigator)) {
		locationOk.value = false;
		locationStatus.value = t("err_location_unavailable");
		return;
	}
	locating.value = true;
	navigator.geolocation.getCurrentPosition(
		async (position) => {
			locating.value = false;
			locationOk.value = true;
			locationStatus.value = t("location_captured");
			const location = {
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
			};
			await store.saveContact(props.token, { geom: location });
			// Reuse RMO proper's reverse geocode to fill the address from the
			// captured point.
			try {
				const code = await geocode.reverseClosest(location);
				if (code?.address?.raw) {
					form.property_address = code.address.raw;
					await flushSave();
				}
			} catch (err) {
				console.error("reverse geocode failed after locate", err);
			}
		},
		(error) => {
			locating.value = false;
			locationOk.value = false;
			locationStatus.value =
				error.code === error.PERMISSION_DENIED
					? t("err_location_denied")
					: t("err_location_unavailable");
		},
		{ enableHighAccuracy: true, timeout: 15000 },
	);
}

// No required fields: everything on this screen is optional and submission
// never gates on it (the district already knows the contact when the check
// was requested from a communication).
async function doSubmit() {
	phoneError.value = (await phoneValidationError(form.phone)) ?? "";
	if (phoneError.value) {
		return;
	}
	submitting.value = true;
	await flushSave();
	const ok = await store.submit(props.token, {
		resident_name: form.name,
		resident_phone: form.phone,
		resident_email: form.email,
	});
	submitting.value = false;
	if (ok) {
		router.push(routes.MosquitoCheckComplete(props.token));
	}
}

function goBeyond() {
	router.push(routes.MosquitoCheckBeyond(props.token));
}

onMounted(() => {
	initFromState();
});
</script>
